<template>
  <div class="flex flex-col h-full overflow-hidden">

    <!-- Message list -->
    <div ref="scrollEl" class="flex-1 overflow-y-auto p-3 space-y-3 min-h-0">

      <div v-if="!messages.length" class="text-xs text-gray-600 text-center pt-6 select-none">
        Ask Claude anything about the open file or project.
      </div>

      <div
        v-for="(msg, i) in messages"
        :key="i"
        class="text-sm"
      >
        <!-- Role label -->
        <div class="text-[10px] font-medium mb-0.5 px-1"
          :class="msg.role === 'user' ? 'text-blue-400' : 'text-green-400'"
        >
          {{ msg.role === 'user' ? 'You' : 'Claude' }}
        </div>

        <!-- Bubble -->
        <div
          class="rounded px-3 py-2 whitespace-pre-wrap leading-relaxed"
          :class="msg.role === 'user'
            ? 'bg-gray-700/60 text-gray-100 ml-4'
            : 'bg-gray-800 text-gray-200 mr-2'"
        >
          {{ msg.role === 'assistant' ? collapseFileBlocks(msg.content) : msg.content }}
        </div>

        <!-- Apply buttons for each <file> block in this message -->
        <div v-if="msg.role === 'assistant' && fileBlocks(msg.content).length" class="mt-1.5 ml-1 flex flex-wrap gap-1">
          <button
            v-for="block in fileBlocks(msg.content)"
            :key="block.path"
            class="text-[11px] px-2 py-0.5 rounded transition-colors"
            :class="{
              'bg-blue-600 hover:bg-blue-500 text-white':   applyState(`${i}:${block.path}`) === 'idle',
              'bg-gray-600 text-gray-400 cursor-wait':       applyState(`${i}:${block.path}`) === 'applying',
              'bg-green-700 text-green-200 cursor-default':  applyState(`${i}:${block.path}`) === 'done',
              'bg-red-800   text-red-300   cursor-default':  applyState(`${i}:${block.path}`) === 'error',
            }"
            :disabled="applyState(`${i}:${block.path}`) !== 'idle'"
            :title="block.path"
            @click="apply(i, block)"
          >
            <template v-if="applyState(`${i}:${block.path}`) === 'idle'">Apply {{ shortPath(block.path) }}</template>
            <template v-else-if="applyState(`${i}:${block.path}`) === 'applying'">Applying…</template>
            <template v-else-if="applyState(`${i}:${block.path}`) === 'done'">✓ Applied</template>
            <template v-else>✗ Failed</template>
          </button>
        </div>
      </div>

      <!-- Thinking indicator -->
      <div v-if="loading" class="flex items-center gap-2 text-xs text-gray-500 ml-1">
        <span class="animate-pulse">Claude is thinking</span>
        <span class="flex gap-0.5">
          <span class="w-1 h-1 rounded-full bg-gray-500 animate-bounce [animation-delay:0ms]"   />
          <span class="w-1 h-1 rounded-full bg-gray-500 animate-bounce [animation-delay:150ms]" />
          <span class="w-1 h-1 rounded-full bg-gray-500 animate-bounce [animation-delay:300ms]" />
        </span>
      </div>

    </div>

    <!-- Input area -->
    <div class="border-t border-gray-700 p-2 shrink-0 space-y-1.5">
      <textarea
        ref="inputEl"
        v-model="inputText"
        class="w-full bg-gray-800 text-gray-100 text-sm rounded p-2 resize-none focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder-gray-600"
        rows="3"
        placeholder="Ask Claude… (Ctrl+Enter to send)"
        :disabled="loading"
        @keydown.ctrl.enter.prevent="send()"
        @keydown.meta.enter.prevent="send()"
      />
      <div class="flex items-center gap-2">
        <span v-if="selectionHint" class="text-[10px] text-blue-400 truncate flex-1 select-none">
          ✂ {{ selectionHint }}
        </span>
        <div v-else class="flex-1" />
        <button
          class="text-[11px] text-gray-500 hover:text-gray-300 px-2 py-0.5 rounded transition-colors disabled:opacity-0"
          :disabled="!messages.length"
          title="Clear conversation"
          @click="clearHistory()"
        >Clear</button>
        <button
          class="text-xs bg-blue-600 hover:bg-blue-500 disabled:opacity-40 px-3 py-1 rounded transition-colors"
          :disabled="loading || !inputText.trim()"
          @click="send()"
        >Send</button>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import {
  sendChatMessage,
  extractFileBlocks,
  collapseFileBlocks,
  type ChatMessage,
  type ClaudeContext,
} from './claudeService'
import { useEditorStore } from '@/stores/editorStore'
import { useRepoStore }   from '@/stores/repoStore'
import { useGitStore }    from '@/stores/gitStore'

const editorStore = useEditorStore()
const repoStore   = useRepoStore()
const gitStore    = useGitStore()

const messages  = ref<ChatMessage[]>([])
const inputText = ref('')
const loading   = ref(false)
const scrollEl  = ref<HTMLElement>()
const inputEl   = ref<HTMLTextAreaElement>()

// Per-block apply state: key is `${messageIndex}:${filePath}`
const applied = ref<Record<string, 'idle' | 'applying' | 'done' | 'error'>>({})

function applyState(key: string): 'idle' | 'applying' | 'done' | 'error' {
  return applied.value[key] ?? 'idle'
}

// Selection hint shown in the toolbar — updated lazily via Monaco's selection event.
// We can't use a computed because Monaco's selection API is not reactive.
const selectionText = ref('')

watch(
  () => editorStore.editorInstance,
  (editor, _prev, onCleanup) => {
    if (!editor) { selectionText.value = ''; return }
    const disposable = editor.onDidChangeCursorSelection(() => {
      const sel = editor.getSelection()
      selectionText.value = sel ? (editor.getModel()?.getValueInRange(sel) ?? '') : ''
    })
    onCleanup(() => disposable.dispose())
  },
  { immediate: true },
)

const selectionHint = computed<string>(() => {
  const len = selectionText.value.length
  if (len === 0) return ''
  const lines = selectionText.value.split('\n').length
  return lines > 1 ? `${lines} lines selected` : `${len} chars selected`
})

// Auto-scroll to bottom whenever messages array changes.
watch(messages, async () => {
  await nextTick()
  if (scrollEl.value) scrollEl.value.scrollTop = scrollEl.value.scrollHeight
}, { deep: true })

function fileBlocks(content: string) {
  return extractFileBlocks(content)
}

function shortPath(filePath: string): string {
  const parts = filePath.split('/')
  return parts.length > 2 ? `…/${parts.slice(-2).join('/')}` : filePath
}

// ── Core send logic ───────────────────────────────────────────────────────────

async function doSend(text: string): Promise<void> {
  if (!text || loading.value) return

  const userMsg: ChatMessage = { role: 'user', content: text }
  messages.value.push(userMsg)
  loading.value = true

  // Capture current editor state at the moment of sending
  const context: ClaudeContext = {
    repo:       repoStore.activeRepo?.name ?? '',
    branch:     gitStore.status?.current   ?? '',
    activeFile: editorStore.activeFile?.path ?? '',
    selection:  selectionText.value || undefined,
  }

  try {
    const reply = await sendChatMessage([...messages.value], context)
    messages.value.push({ role: 'assistant', content: reply })
  } catch (err) {
    messages.value.push({
      role:    'assistant',
      content: `Error: ${err instanceof Error ? err.message : String(err)}`,
    })
  } finally {
    loading.value = false
  }
}

/** Send the current inputText (or an optional prefill string). */
async function send(prefill?: string): Promise<void> {
  const text = (prefill ?? inputText.value).trim()
  if (!text) return
  inputText.value = ''
  await doSend(text)
  await nextTick()
  inputEl.value?.focus()
}

function clearHistory(): void {
  messages.value = []
  applied.value  = {}
}

// ── Apply <file> blocks to Monaco ────────────────────────────────────────────

async function apply(msgIdx: number, block: { path: string; content: string }): Promise<void> {
  const key = `${msgIdx}:${block.path}`
  applied.value = { ...applied.value, [key]: 'applying' }

  // Find an already-open file whose path matches (exact or as a suffix of the workspace path)
  let openFile = editorStore.openFiles.find(
    f => f.path === block.path || f.path.endsWith('/' + block.path),
  )

  // If not open, try to fetch it so we get a Monaco model to update
  if (!openFile) {
    try {
      await editorStore.openFile(block.path)
      openFile = editorStore.openFiles.find(f => f.path === block.path)
    } catch {
      applied.value = { ...applied.value, [key]: 'error' }
      return
    }
  }

  if (!openFile) {
    applied.value = { ...applied.value, [key]: 'error' }
    return
  }

  const model = editorStore.getModel(openFile.path)
  if (!model) {
    applied.value = { ...applied.value, [key]: 'error' }
    return
  }

  // pushEditOperations makes the change undoable via Ctrl+Z inside Monaco.
  // Replacing the full model range with the new content.
  model.pushEditOperations(
    null,
    [{ range: model.getFullModelRange(), text: block.content }],
    () => null,
  )
  editorStore.markDirty(openFile.path)

  // Switch to the updated file so the user sees the applied changes immediately
  editorStore.activeFile = openFile

  applied.value = { ...applied.value, [key]: 'done' }
}

defineExpose({ send, clearHistory })
</script>
